import React from "react";
import {
  CommercialPage,
  PageIntro,
  ContactCTA,
  SectionNav,
} from "../components/commercial";
import { commercialMetadata } from "./commercial-metadata";
const services = [
  [
    "embedded",
    "Embedded & IoT systems",
    "Connected devices that work in the field.",
    "Build and evolve Embedded Linux and connected-device software, from Raspberry Pi and ARM applications to provisioning, fleet management, OTA updates and remote diagnostics. Work across Linux services, networking, BLE, peripherals and device-to-cloud communication.",
    "For deployed devices that are hard to manage, fragile software stacks, or failures that cross hardware and software boundaries.",
  ],
  [
    "ai",
    "AI & intelligent systems",
    "Take AI from prototype to production.",
    "Engineer LLM applications, agents, RAG, intelligent automation and model integrations as parts of a complete system. Design context, retrieval, orchestration, data pipelines, failure handling, evaluation and observability, including local or edge AI where appropriate.",
    "For an AI feature that needs reliable product behavior, or a workflow connecting models, APIs and existing business logic.",
  ],
  [
    "backend",
    "Backend & distributed systems",
    "Connect products, devices, data and operations.",
    "Design backend platforms, REST APIs, asynchronous workflows, device backends and integration services. Consider interfaces, dependencies, failure modes, deployment, performance and maintainability together.",
    "For integrations that have become difficult to maintain, manual operations that need automation, or products that need a dependable backend.",
  ],
  [
    "mobile",
    "Mobile & product engineering",
    "Build and evolve production Android software.",
    "Develop Kotlin and Android products with backend services, AI, analytics, subscriptions and purchase flows. Address architecture, modernization, deployment, crash monitoring and the ongoing evolution of the application.",
    "For products that need technical ownership across the complete stack, beyond individual screens.",
  ],
  [
    "infrastructure",
    "IT infrastructure & server administration",
    "Keep the operating environment reliable.",
    "Configure, administer and troubleshoot Linux, Windows and Unix servers across on-premises, cloud and hybrid environments. Cover access, hardening, networking, services, virtual machines, containers, backups, restoration testing, monitoring, migrations and automation.",
    "For unstable servers, risky migrations, untested recovery, or infrastructure dependent on undocumented manual work. Ongoing administration has agreed responsibilities, support availability and service scope.",
  ],
];
export default function Services() {
  return (
    <CommercialPage meta={commercialMetadata.services}>
      <PageIntro
        label="Engineering services"
        title="Production software for complex systems."
      >
        Senior technical ownership from architecture and problem solving through
        implementation, integration, deployment and production operation.
        Engagements are built around concrete engineering outcomes.
      </PageIntro>
      <SectionNav
        links={[
          ["#capabilities", "Capabilities"],
          ["#architecture", "Architecture & integration"],
        ]}
      />
      <section id="capabilities" className="section services-section">
        <div className="container">
          <div className="section-heading">
            <h2>Where we can help.</h2>
          </div>
          <div className="service-list">
            {services.map(([id, title, promise, copy, fit], index) => (
              <article id={id} className="service-row capability-row" key={id}>
                <span className="service-number">0{index + 1}</span>
                <h3>{title}</h3>
                <div>
                  <h4>{promise}</h4>
                  <p>{copy}</p>
                  <p>{fit}</p>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>
      <section id="architecture" className="section container about-grid">
        <div>
          <p className="eyebrow">Across the whole system</p>
          <h2>Architecture & technical problem solving.</h2>
          <a className="text-link page-action" href="/engineering">
            Explore our engineering approach <span aria-hidden="true">↗</span>
          </a>
        </div>
        <div className="page-prose">
          <p>
            Some problems cross Embedded Linux, device software, networking,
            backend, AI and user applications. You do not need to divide the
            problem into technology categories before asking for help.
          </p>
          <p>
            We can review architecture, validate feasibility, investigate
            failures, define integration strategies and plan modernization.
            Existing systems and deployed constraints are part of the
            engineering problem.
          </p>
          <p>
            Technical leadership comes from Pablo Rego’s more than 20 years
            across embedded and high-integrity systems, aerospace, industrial
            R&D, AI, backend software and production products.
          </p>
          <p>
            After qualification, every continuing engagement begins with a paid
            Engineering Assessment before the implementation Proposal/quotation.
            It establishes a clearer technical direction and the basis for a
            defined project.
          </p>
          <a className="text-link" href="/how-we-work">
            How engagements work <span aria-hidden="true">↗</span>
          </a>
        </div>
      </section>
      <ContactCTA title="What does your system need to do?">
        Tell us what you are building, what is not working, or what technical
        outcome you need to achieve.
      </ContactCTA>
    </CommercialPage>
  );
}
